
import React, { useMemo, useEffect } from 'react';
import { Block, BlockType, Manifest, PublicationType } from '../../types';
import { MoreHorizontal, Heart, MessageCircle, Share2, EyeOff, File as FileIcon, X, Send, Music2, CheckCircle2, Trophy } from 'lucide-react';
import { stripMarkdown, parseMarkdownToHtml } from '../../utils/markdownUtils';

interface SocialPreviewProps {
    blocks: Block[];
    activeNetwork?: {
        id: string;
        name: string;
        manifest: Manifest;
    };
    publicationType?: PublicationType;
}

interface RenderableMedia {
    type: 'photo' | 'video';
    url: string;
    isBlob: boolean;
}

const MediaFileRenderer = React.memo(({ item, className, cover = true }: { item: RenderableMedia, className?: string, cover?: boolean }) => {
    const src = item.url;

    useEffect(() => {
        return () => {
            if (item.isBlob && src.startsWith('blob:')) {
                // Only revoke if we created it locally in this component context (isBlob=true)
                // However, if the URL comes from EditorFile (which creates its own blob URL), 
                // we should probably NOT revoke it here as the Editor owns it.
                // The logic in normalizedMedia sets isBlob=true only for raw Files.
                URL.revokeObjectURL(src);
            }
        };
    }, [src, item.isBlob]);

    if (item.type === 'photo') {
        return <img src={src} alt="" className={className} style={{ objectFit: cover ? 'cover' : 'contain', width: '100%', height: '100%' }} />;
    }
    
    return (
        <video 
            src={src} 
            className={className}
            style={{ objectFit: cover ? 'cover' : 'contain', width: '100%', height: '100%' }}
            controls={false}
            muted
            autoPlay
            loop
            playsInline
        />
    );
});

export const SocialPreview: React.FC<SocialPreviewProps> = ({ blocks, activeNetwork, publicationType = 'feed' }) => {
    
    const isBlockSupported = (type: BlockType) => {
        if (!activeNetwork) return true;
        return activeNetwork.manifest.supports.blocks.includes(type);
    };

    const renderTextContent = (markdownContent: string) => {
        if (!markdownContent) return <span className="text-slate-300 italic">Текст публикации...</span>;

        const supportsFormatting = activeNetwork 
            ? activeNetwork.manifest.supports.textFormatting?.some(f => ['markdown', 'html'].includes(f))
            : true;

        if (supportsFormatting) {
            return (
                <div 
                    className="rich-text-preview" 
                    dangerouslySetInnerHTML={parseMarkdownToHtml(markdownContent)} 
                />
            );
        } else {
            return <div className="whitespace-pre-wrap">{stripMarkdown(markdownContent)}</div>;
        }
    };

    const textBlock = blocks.find(b => b.type === 'text');
    const mediaBlock = blocks.find(b => b.type === 'media');
    const fileBlock = blocks.find(b => b.type === 'file');
    const pollBlock = blocks.find(b => b.type === 'poll');

    const normalizedMedia: RenderableMedia[] = useMemo(() => {
        if (!mediaBlock) return [];
        
        // Structure from API: { type: 'media', items: [{type, source}] }
        if ((mediaBlock as any).items) {
            return (mediaBlock as any).items.map((item: any) => ({
                type: item.type === 'video' ? 'video' : 'photo',
                url: item.source,
                isBlob: false
            }));
        }
        
        // Structure from Wizard: { type: 'media', content: { files: [File | EditorFile] } }
        if (mediaBlock.content?.files) {
            return mediaBlock.content.files.map((file: any) => {
                // Case 1: EditorFile (from BlockEditors) - has .url property
                if (file.url) {
                    const isVideo = file.type?.startsWith('video') || file.type === 'video';
                    return {
                        type: isVideo ? 'video' : 'photo',
                        url: file.url,
                        isBlob: false // Lifecycle managed by Editor
                    };
                }
                
                // Case 2: Raw File (fallback)
                if (file instanceof File) {
                    return {
                        type: file.type.startsWith('video/') ? 'video' : 'photo',
                        url: URL.createObjectURL(file),
                        isBlob: true // We own this URL
                    };
                }
                
                return null;
            }).filter((item: any) => item !== null) as RenderableMedia[];
        }

        return [];
    }, [mediaBlock]);

    const normalizedFiles = useMemo(() => {
        if (!fileBlock) return [];
        if ((fileBlock as any).items) return (fileBlock as any).items;
        if (fileBlock.content?.files) {
            return fileBlock.content.files.map((f: any) => ({ 
                name: f.name, 
                size: f.size 
            }));
        }
        return [];
    }, [fileBlock]);

    const renderUnsupportedOverlay = (label: string) => (
        <div className="absolute inset-0 z-10 bg-slate-100/90 backdrop-blur-[1px] flex flex-col items-center justify-center text-center p-4 border-2 border-dashed border-slate-300 rounded-lg">
            <EyeOff className="w-8 h-8 text-slate-400 mb-2" />
            <span className="text-sm font-bold text-slate-600">Не отобразится в {activeNetwork?.name}</span>
            <span className="text-xs text-slate-400 mt-1">Тип контента "{label}" не поддерживается</span>
        </div>
    );

    const FeedPreview = () => (
        <div className="w-full max-w-[400px] mx-auto bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden font-sans">
             <style>{`
                .rich-text-preview a { color: #2563eb; text-decoration: none; }
                .rich-text-preview a:hover { text-decoration: underline; }
                .rich-text-preview blockquote { border-left: 3px solid #cbd5e1; padding-left: 0.5rem; color: #475569; font-style: italic; margin: 0.5em 0; }
                .rich-text-preview pre { background: #f1f5f9; padding: 4px; border-radius: 4px; overflow-x: auto; margin: 0.5em 0; }
                .rich-text-preview code { background: #f1f5f9; padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 0.9em; color: #d63384; }
                .rich-text-preview s, .rich-text-preview strike { text-decoration: line-through; color: #64748b; }
                .rich-text-preview p { margin-bottom: 0.5em; }
                .rich-text-preview p:last-child { margin-bottom: 0; }
                .rich-text-preview .spoiler, .rich-text-preview .tg-spoiler { 
                    background-color: #334155; color: transparent; border-radius: 3px; cursor: pointer; padding: 0 2px; transition: all 0.2s;
                }
                .rich-text-preview .spoiler:hover, .rich-text-preview .tg-spoiler:hover { background-color: #f1f5f9; color: inherit; }
            `}</style>

            <div className="p-3 flex items-center justify-between border-b border-slate-50">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                        YOU
                    </div>
                    <div>
                        <div className="font-bold text-slate-800 text-sm leading-tight">Ваш Аккаунт</div>
                        <div className="text-xs text-slate-400">Только что</div>
                    </div>
                </div>
                <MoreHorizontal className="w-5 h-5 text-slate-400" />
            </div>

            <div className="pb-4">
                {textBlock && (
                    <div className="relative">
                        <div className={`px-4 py-3 text-sm text-slate-800 leading-relaxed ${!isBlockSupported('text') ? 'opacity-20' : ''}`}>
                            {renderTextContent(textBlock.content?.text || (textBlock as any).content)}
                        </div>
                        {!isBlockSupported('text') && renderUnsupportedOverlay('Текст')}
                    </div>
                )}

                {normalizedMedia.length > 0 && (
                    <div className="relative mt-2">
                         <div className={`grid gap-0.5 ${normalizedMedia.length === 1 ? 'grid-cols-1' : 'grid-cols-2'} ${!isBlockSupported('media') ? 'opacity-20' : ''}`}>
                            {normalizedMedia.slice(0, 4).map((item, idx) => (
                                <div key={idx} className="aspect-square bg-slate-100 relative overflow-hidden">
                                    <MediaFileRenderer item={item} className="absolute inset-0" />
                                </div>
                            ))}
                        </div>
                         {!isBlockSupported('media') && renderUnsupportedOverlay('Медиа')}
                    </div>
                )}

                {normalizedFiles.length > 0 && (
                    <div className="relative px-4 mt-2">
                        <div className={`space-y-1 ${!isBlockSupported('file') ? 'opacity-20' : ''}`}>
                             {normalizedFiles.map((file: any, i: number) => (
                                 <div key={i} className="flex items-center p-2 rounded-lg bg-slate-50 border border-slate-200 gap-3">
                                     <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center text-blue-500">
                                         <FileIcon className="w-4 h-4" />
                                     </div>
                                     <div className="flex-1 min-w-0">
                                         <div className="text-xs font-bold text-slate-700 truncate">{file.name || 'File'}</div>
                                         {file.size && <div className="text-[9px] text-slate-400">{(file.size / 1024 / 1024).toFixed(2)} MB</div>}
                                     </div>
                                 </div>
                             ))}
                        </div>
                        {!isBlockSupported('file') && renderUnsupportedOverlay('Файлы')}
                    </div>
                )}

                {pollBlock && (
                    <div className="relative px-4 mt-3">
                        <div className={`bg-slate-50 rounded-lg border border-slate-200 p-4 ${!isBlockSupported('poll') ? 'opacity-20' : ''}`}>
                             <div className="font-bold text-sm text-slate-800 mb-3 flex items-start gap-2">
                                 {(pollBlock.content?.quiz || (pollBlock as any).quiz) && (
                                     <Trophy className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                                 )}
                                 {pollBlock.content?.question || (pollBlock as any).question || "Вопрос опроса..."}
                             </div>
                             <div className="space-y-2">
                                 {(pollBlock.content?.options || (pollBlock as any).options || ["", ""]).map((opt: string, i: number) => (
                                     opt && (
                                         <div key={i} className={`w-full py-2 px-3 rounded-md border text-xs font-medium text-center shadow-sm relative overflow-hidden ${
                                             (pollBlock.content?.quiz || (pollBlock as any).quiz) && (pollBlock.content?.correctOptionId === i || (pollBlock as any).correctOptionId === i)
                                             ? 'bg-green-50 border-green-200 text-green-700'
                                             : 'bg-white border-slate-200 text-slate-600'
                                         }`}>
                                             {opt}
                                             {(pollBlock.content?.quiz || (pollBlock as any).quiz) && (pollBlock.content?.correctOptionId === i || (pollBlock as any).correctOptionId === i) && (
                                                 <div className="absolute right-2 top-1/2 -translate-y-1/2">
                                                     <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                                                 </div>
                                             )}
                                         </div>
                                     )
                                 ))}
                             </div>
                             <div className="mt-3 text-[10px] text-slate-400 text-center uppercase tracking-wide">
                                 {(pollBlock.content?.quiz || (pollBlock as any).quiz) ? 'Викторина' : 'Анонимный опрос'}
                             </div>
                        </div>
                        {!isBlockSupported('poll') && renderUnsupportedOverlay('Опрос')}
                    </div>
                )}
            </div>

            <div className="px-4 py-3 border-t border-slate-50 flex items-center justify-between text-slate-500">
                <div className="flex gap-4">
                    <Heart className="w-6 h-6 hover:text-red-500 transition-colors cursor-pointer" />
                    <MessageCircle className="w-6 h-6 hover:text-blue-500 transition-colors cursor-pointer" />
                </div>
                <Share2 className="w-6 h-6 hover:text-slate-800 transition-colors cursor-pointer" />
            </div>
        </div>
    );

    const StoryPreview = () => {
        const primaryMedia = normalizedMedia[0];
        const hasMedia = !!primaryMedia;

        return (
            <div className="w-[300px] h-[533px] mx-auto bg-black rounded-3xl overflow-hidden relative shadow-2xl border-4 border-slate-800">
                {hasMedia ? (
                     <div className="absolute inset-0">
                        <MediaFileRenderer item={primaryMedia} className="absolute inset-0 w-full h-full" />
                     </div>
                ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 flex items-center justify-center p-8 text-center">
                        {textBlock && (textBlock.content?.text || (textBlock as any).content) ? (
                            <span className="text-white font-bold text-lg drop-shadow-md whitespace-pre-wrap">
                                {stripMarkdown(textBlock.content?.text || (textBlock as any).content).substring(0, 150)}
                            </span>
                        ) : (
                            <span className="text-white/50 text-sm font-medium">Нет контента</span>
                        )}
                    </div>
                )}

                <div className="absolute inset-0 p-4 flex flex-col justify-between bg-gradient-to-b from-black/40 via-transparent to-black/20 pointer-events-none">
                    <div>
                        <div className="flex gap-1 mb-3">
                            <div className="h-0.5 bg-white/80 flex-1 rounded-full"></div>
                            <div className="h-0.5 bg-white/30 flex-1 rounded-full"></div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-white/20 p-0.5">
                                    <div className="w-full h-full bg-slate-200 rounded-full flex items-center justify-center text-xs font-bold text-slate-600">
                                        YOU
                                    </div>
                                </div>
                                <span className="text-white font-bold text-sm text-shadow-sm">Ваш Аккаунт</span>
                                <span className="text-white/60 text-xs">2ч</span>
                            </div>
                            <X className="w-6 h-6 text-white" />
                        </div>
                    </div>

                    {hasMedia && textBlock && (textBlock.content?.text || (textBlock as any).content) && (
                        <div className="self-center bg-black/50 backdrop-blur-sm px-4 py-2 rounded-lg max-w-[80%] text-center">
                            <span className="text-white font-bold text-sm">
                                {stripMarkdown(textBlock.content?.text || (textBlock as any).content).substring(0, 50)}...
                            </span>
                        </div>
                    )}
                    
                    <div className="flex items-center gap-3 mt-4">
                        <div className="flex-1 h-11 rounded-full border border-white/30 flex items-center px-4">
                            <span className="text-white/70 text-sm">Отправить сообщение</span>
                        </div>
                        <Heart className="w-7 h-7 text-white" />
                        <Send className="w-7 h-7 text-white" />
                    </div>
                </div>
            </div>
        );
    };

    const ReelPreview = () => {
        const primaryMedia = normalizedMedia[0];
        const hasMedia = !!primaryMedia;

        return (
            <div className="w-[300px] h-[533px] mx-auto bg-black rounded-3xl overflow-hidden relative shadow-2xl border-4 border-slate-800">
                 {hasMedia ? (
                     <div className="absolute inset-0">
                        <MediaFileRenderer item={primaryMedia} className="absolute inset-0 w-full h-full" />
                     </div>
                ) : (
                    <div className="absolute inset-0 bg-slate-800 flex items-center justify-center">
                        <span className="text-white/30 text-xs uppercase font-bold tracking-widest">Нет видео</span>
                    </div>
                )}

                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80 pointer-events-none">
                    
                    <div className="absolute top-4 right-4 text-white">
                        <div className="w-6 h-6 border-2 border-white rounded-md"></div>
                    </div>

                    <div className="absolute bottom-20 right-2 flex flex-col items-center gap-6">
                        <div className="flex flex-col items-center gap-1">
                            <Heart className="w-8 h-8 text-white" />
                            <span className="text-white text-xs font-bold">12K</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <MessageCircle className="w-8 h-8 text-white" />
                            <span className="text-white text-xs font-bold">534</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <Send className="w-8 h-8 text-white -rotate-12 translate-x-1" />
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <MoreHorizontal className="w-8 h-8 text-white" />
                        </div>
                    </div>

                    <div className="absolute bottom-4 left-4 right-14 text-white text-left">
                        <div className="flex items-center gap-2 mb-2">
                             <div className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-xs font-bold text-slate-800">
                                 Y
                             </div>
                             <span className="font-bold text-sm">Ваш Аккаунт</span>
                             <button className="border border-white/50 rounded-md px-2 py-0.5 text-[10px] font-bold ml-2">
                                 Follow
                             </button>
                        </div>
                        
                        {textBlock && (textBlock.content?.text || (textBlock as any).content) && (
                            <p className="text-sm line-clamp-2 opacity-90 mb-2 font-medium">
                                {stripMarkdown(textBlock.content?.text || (textBlock as any).content)}
                            </p>
                        )}
                        
                        <div className="flex items-center gap-2 opacity-70">
                            <Music2 className="w-3 h-3" />
                            <span className="text-xs marquee">Оригинальная аудиодорожка • Ваш Аккаунт</span>
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    return (
        <div className="animate-in fade-in duration-300">
            {publicationType === 'story' && <StoryPreview />}
            {publicationType === 'reel' && <ReelPreview />}
            {publicationType === 'feed' && <FeedPreview />}

            <div className="mt-4 text-center">
                {activeNetwork ? (
                     <div className="inline-block bg-blue-50 px-3 py-1 rounded-full text-[10px] text-blue-600 font-bold border border-blue-100">
                        Предпросмотр для {activeNetwork.name} ({publicationType})
                    </div>
                ) : (
                    <div className="inline-block bg-slate-100 px-3 py-1 rounded-full text-[10px] text-slate-500 font-bold border border-slate-200">
                        Универсальный предпросмотр ({publicationType})
                    </div>
                )}
            </div>
        </div>
    );
};